import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useApi } from "@/hooks/useApi";
import { api } from "@/lib/api";
import {
	IconBrandTelegram,
	IconCamera,
	IconChevronRight,
	IconLock,
	IconLogout,
	IconMessageCircle,
	IconUser,
	IconUserPlus,
} from "@tabler/icons-react";
import { Card, ErrorBlock } from "../components/Ui";
import { toast } from "../lib/toast";
import { getInitials } from "../lib/initials";

export default function Profile() {
	const navigate = useNavigate();
	const { isLoaded, isSignedIn, signOut, session, getToken } = useAuth();
	const fileInputRef = useRef<HTMLInputElement>(null);
	const user = session.data?.user;

	const [uploading, setUploading] = useState(false);
	const [uploadError, setUploadError] = useState<string | null>(null);

	const premiumStatus = useApi((token) => api.premiumStatus(token), []);
	const isPremium = premiumStatus.data?.isPremium === true;

	const telegramStatus = useApi((token) => api.telegramStatus(token), []);
	const telegramConnected = telegramStatus.data?.connected === true;

	const meApi = useApi((t) => api.me(t), []);
	const hasCouple = Boolean(meApi.data?.couple?.paired);

	if (!isLoaded) return null;
	if (!isSignedIn || !user) {
		return <ErrorBlock message="Kamu belum masuk." />;
	}

	const fullName = user.name ?? "";
	const initials = getInitials(fullName || user.email);
	const userId = user.id;
	const hasPhoto = Boolean(user.image);
	const imageUrl = hasPhoto ? `/assets/avatars/${userId}?v=${encodeURIComponent(user.image ?? "")}` : null;
	const email = user.email;

	async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
		const file = e.target.files?.[0];
		if (!file) return;
		if (file.size > 5 * 1024 * 1024) {
			setUploadError("Ukuran file maksimal 5 MB");
			toast.error("File terlalu besar", { description: "Maksimal 5 MB" });
			return;
		}
		setUploading(true);
		setUploadError(null);
		try {
			const token = await getToken();
			const form = new FormData();
			form.append("file", file);
			const res = await fetch("/api/me/photo", {
				method: "POST",
				body: form,
				headers: token ? { Authorization: `Bearer ${token}` } : {},
				credentials: "include",
			});
			if (!res.ok) {
				const text = await res.text();
				throw new Error(text || "Upload gagal");
			}
			toast.success("Foto profil diperbarui");
			window.location.reload();
		} catch (err) {
			const msg =
				err instanceof Error ? err.message : "Gagal mengunggah foto";
			setUploadError(msg);
			toast.error("Gagal mengunggah foto", { description: msg });
		} finally {
			setUploading(false);
			if (fileInputRef.current) fileInputRef.current.value = "";
		}
	}

	return (
		<div style={{ display: "grid", gap: "0.75rem" }}>
			<Card style={{ textAlign: "center", padding: "1rem" }}>
				<div
					style={{
						position: "relative",
						width: 96,
						height: 96,
						margin: "0 auto 0.5rem",
					}}
				>
					<div
						style={{
							width: 96,
							height: 96,
							borderRadius: "50%",
							background: "var(--color-primary-soft)",
							color: "var(--color-primary)",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							fontSize: "2rem",
							fontWeight: 800,
							overflow: "hidden",
							border: isPremium ? "3px solid transparent" : "2px solid var(--color-border)",
							backgroundImage: isPremium ? "linear-gradient(var(--color-surface), var(--color-surface)), linear-gradient(135deg, #93c5fd, #3b82f6, #1d4ed8)" : undefined,
							backgroundOrigin: isPremium ? "border-box" : undefined,
							backgroundClip: isPremium ? "padding-box, border-box" : undefined,
						}}
					>
						{imageUrl ? (
							<img
								src={imageUrl}
								alt={fullName || "Avatar"}
								style={{
									width: "100%",
									height: "100%",
									objectFit: "cover",
								}}
							/>
						) : (
							<span aria-hidden>{initials}</span>
						)}
					</div>
					<button
						type="button"
						className="btn btn-primary"
						onClick={() => fileInputRef.current?.click()}
						disabled={uploading}
						aria-label="Ubah foto"
						style={{
							position: "absolute",
							right: -4,
							bottom: -4,
							width: 32,
							height: 32,
							padding: 0,
							borderRadius: "50%",
						}}
					>
						{uploading ? (
							<span
								className="spinner"
								style={{ width: 14, height: 14, borderWidth: 2 }}
							/>
						) : (
							<IconCamera size={14} />
						)}
					</button>
					<input
						ref={fileInputRef}
						type="file"
						accept="image/png,image/jpeg,image/webp"
						onChange={onFile}
						style={{ display: "none" }}
					/>
				</div>
				{isPremium && (
					<div style={{
						display: "inline-flex", alignItems: "center", gap: "0.25rem",
						marginTop: "0.375rem", padding: "0.175rem 0.625rem",
						borderRadius: "var(--radius-full)",
						background: "linear-gradient(135deg, #60a5fa, #2563eb)",
						color: "#fff", fontSize: "0.7rem", fontWeight: 700,
					}}>
						💎 Premium
					</div>
				)}
				<h3 style={{ margin: "0.25rem 0 0" }}>
					{fullName || email || "Pengguna"}
				</h3>
				{email && (
					<p
						className="muted"
						style={{ margin: "0.15rem 0 0", fontSize: "0.85rem" }}
					>
						{email}
					</p>
				)}
				{uploadError && <ErrorBlock message={uploadError} />}
			</Card>

			<MenuSection>
				<MenuItem
					first
					icon={<IconUser size={18} color="var(--color-primary)" />}
					label="Ubah nama"
					onClick={() => navigate("/settings/name")}
				/>
				<MenuItem
					icon={<IconLock size={18} color="var(--color-primary)" />}
					label="Ubah kata sandi"
					onClick={() => navigate("/settings/password")}
				/>
				<MenuItem
					icon={<IconMessageCircle size={18} color="var(--color-primary)" />}
					label="Pengaturan Chat"
					onClick={() => navigate("/chat/settings")}
				/>
				<MenuItem
					icon={<IconBrandTelegram size={18} color="var(--color-primary)" />}
					label="Notifikasi Telegram"
					badge={telegramConnected ? "Aktif" : undefined}
					onClick={() => navigate("/settings/telegram")}
				/>
				{!meApi.loading && !hasCouple && (
					<MenuItem
						icon={<IconUserPlus size={18} color="var(--color-primary)" />}
						label="Ajak Pasangan"
						onClick={() => navigate("/invite")}
					/>
				)}
			</MenuSection>

			<button
				type="button"
				className="btn btn-danger"
				onClick={async () => {
					await signOut();
					navigate("/");
				}}
				style={{ width: "100%", marginTop: "0.5rem" }}
			>
				<IconLogout size={16} /> Keluar
			</button>
		</div>
	);
}

function MenuSection({ children }: { children: React.ReactNode }) {
	return (
		<Card style={{ padding: 0, overflow: "hidden" }}>
			<div style={{ display: "grid" }}>{children}</div>
		</Card>
	);
}

function MenuItem({
	icon,
	label,
	onClick,
	first,
	badge,
}: {
	icon: React.ReactNode;
	label: string;
	onClick: () => void;
	first?: boolean;
	badge?: string;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			style={{
				display: "flex",
				alignItems: "center",
				gap: "0.65rem",
				padding: "0.75rem 0.9rem",
				background: "transparent",
				border: "none",
				borderTop: first
					? "none"
					: "1px solid var(--color-border)",
				borderRadius: 0,
				color: "var(--color-text)",
				fontFamily: "inherit",
				fontSize: "0.92rem",
				fontWeight: 500,
				textAlign: "left",
				cursor: "pointer",
				width: "100%",
			}}
		>
			<span
				style={{
					width: 32,
					height: 32,
					borderRadius: "var(--radius-full)",
					background: "var(--color-primary-soft)",
					display: "inline-flex",
					alignItems: "center",
					justifyContent: "center",
					flexShrink: 0,
				}}
			>
				{icon}
			</span>
			<span style={{ flex: 1, minWidth: 0 }}>{label}</span>
			{badge && (
				<span style={{
					fontSize: "0.72rem",
					fontWeight: 600,
					color: "var(--color-success)",
					marginRight: "0.25rem",
				}}>
					{badge}
				</span>
			)}
			<IconChevronRight
				size={16}
				color="var(--color-text-subtle)"
				stroke={1.8}
			/>
		</button>
	);
}
